import { useEffect, useMemo, useState } from 'react';
import generate from './MandGen';
import { entryRenderDims } from './renderSize';
import { getThumb } from './thumbs';
import yieldToPaint from './yieldToPaint';

// Re-render one saved entry at full size from its coordinates alone.
//
// Sized to the entry's own bounds aspect rather than the viewport's, so a region
// found on a desktop is not stretched when viewed on a phone — see renderSize.js.
// Rendered once per entry and left to CSS to scale, matching how the generator
// treats its own image across a resize.
//
// `placeholder` is the cached thumbnail, available immediately, so a gallery
// entry appears at once (soft, then sharp) instead of showing a void for a second.
const useRenderedEntry = (entry) => {

    const id = entry ? entry.id : null;

    const [rendered, setRendered] = useState({ id: null, dataUrl: null });
    const [rendering, setRendering] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!entry) {
            setRendered({ id: null, dataUrl: null });
            setRendering(false);
            setFailed(false);
            return;
        }

        // renderRegion cannot be interrupted once it starts, so a change of entry
        // mid-render is handled by discarding the result rather than aborting.
        let cancelled = false;

        setRendering(true);
        setFailed(false);

        (async () => {
            await yieldToPaint();
            if (cancelled) return;

            let result = null;
            try {
                const { width, height } = entryRenderDims(entry);
                result = generate({
                    manual: true,
                    width,
                    height,
                    axisValues: {
                        newMinR: entry.realMin,
                        newMaxR: entry.realMax,
                        newMinI: entry.imaginaryMin,
                        newMaxI: entry.imaginaryMax
                    }
                });
            } catch {
                if (!cancelled) setFailed(true);
            }

            if (cancelled) return;
            if (result) setRendered({ id: entry.id, dataUrl: result.dataUrl });
            setRendering(false);
        })();

        return () => { cancelled = true; };
        // Bounds are a function of the id, so the id alone is the full dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const isCurrent = rendered.id === id && rendered.dataUrl !== null;

    // Memoised on the id, not read per render: the thumbnail cache is around a
    // megabyte of JSON, and this component re-renders on every gallery change,
    // paging step and button press.
    const placeholder = useMemo(() => (id ? getThumb(id) : null), [id]);

    return {
        dataUrl: isCurrent ? rendered.dataUrl : null,
        placeholder,
        rendering: rendering || (!isCurrent && !failed && id !== null),
        failed
    };
};

export default useRenderedEntry;
